import { Backdrop } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import { useLoggedInUserRoles } from '~/components/Auth/useLoggedInUserRoles';
import { Employees } from '~/data/employees/types';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { useSearchDimensions } from '~/legacy-components/ModulePageFooter/components/EmployeesSearch/useSearchDimensions';
import { InfoBox } from '~/legacy-components/ModulePageFooter/components/InfoBox';
import {
  isRoleITAdmin,
  useHasAccessOverEmployee,
} from '~/services/Permissions';

import { CategorySearchItem } from './components/CategorySearchItem';
import { LegacyModuleIconTabs } from './components/LegacyModuleIconTabs';
import { SearchedItem } from './components/SearchedItem';
import { SearchFooterComponent } from './components/SearchFooterComponent';
import { useEmployeesSearch } from './useEmployeesSearch';
import { GroupedOrgUnitEmps } from './useSearchData';
import {
  ALL_EMPS_SEARCH_TAB_INDEX,
  ENDED_EMPS_SEARCH_TAB_INDEX,
  HR_ADMIN_OVERVIEW_SEARCH_TAB_INDEX,
  MATRIX_TEAM_SEARCH_TAB_INDEX,
  NEW_EMPS_SEARCH_TAB_INDEX,
  ORG_UNITS_SEARCH_TAB_INDEX,
  RESULTS_SEARCH_TAB_INDEX,
  SEND_EMAIL_SEARCH_TAB_INDEX,
  TEAM_SEARCH_TAB_INDEX,
} from './useSearchUI';

type Props = {
  onEmailClick: () => void;
};

export const EmployeesSearch = ({ onEmailClick }: Props) => {
  const { t } = useTranslation();
  const roles = useLoggedInUserRoles();
  const { hasFullAccessOverEmp, isMgrOrHigherMgr } = useHasAccessOverEmployee();
  const {
    listItems,
    currentTab,
    isBoxOpen,
    isPinned,
    isHRAdmin,
    showEditableEndedEmployeeTab,
    showMatrixTeamTab,
    showMyTeamTab,
    showNewEmployeeTab,
    onClose,
    onListToggle,
    onPin,
    onTabChange,
    onSearch,
  } = useEmployeesSearch(onEmailClick);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const allTabs = useMemo(
    () => [
      {
        tabId: ALL_EMPS_SEARCH_TAB_INDEX,
        href: '',
        label: t('laAllEmployees'),
        active: currentTab === ALL_EMPS_SEARCH_TAB_INDEX,
        icon: <LegacyFAIcon FAIcon={LegacyFAIcons.Users} />,
        shown: true,
      },
      {
        tabId: TEAM_SEARCH_TAB_INDEX,
        href: '',
        label: t('laTeam'),
        active: currentTab === TEAM_SEARCH_TAB_INDEX,
        icon: <i className="fa">T</i>,
        shown: showMyTeamTab,
      },
      {
        tabId: MATRIX_TEAM_SEARCH_TAB_INDEX,
        href: '',
        label: t('laMatrixTeam'),
        active: currentTab === MATRIX_TEAM_SEARCH_TAB_INDEX,
        icon: <i className="fa">T2</i>,
        shown: showMatrixTeamTab,
      },
      {
        tabId: ORG_UNITS_SEARCH_TAB_INDEX,
        href: '',
        label: t('laOrganization'),
        active: currentTab === ORG_UNITS_SEARCH_TAB_INDEX,
        icon: <LegacyFAIcon FAIcon={LegacyFAIcons.Sitemap} />,
        shown: true,
      },
      {
        tabId: NEW_EMPS_SEARCH_TAB_INDEX,
        href: '',
        label: t('laNewEmployee'),
        active: currentTab === NEW_EMPS_SEARCH_TAB_INDEX,
        icon: <LegacyFAIcon FAIcon={LegacyFAIcons.PlusSquare} />,
        shown: showNewEmployeeTab,
      },
      {
        tabId: ENDED_EMPS_SEARCH_TAB_INDEX,
        href: '',
        label: t('laEndedEmployee'),
        active: currentTab === ENDED_EMPS_SEARCH_TAB_INDEX,
        icon: <LegacyFAIcon FAIcon={LegacyFAIcons.MinusSquare} />,
        shown: showEditableEndedEmployeeTab,
      },
      {
        tabId: RESULTS_SEARCH_TAB_INDEX,
        href: '',
        label: t('laSearch'),
        active: currentTab === RESULTS_SEARCH_TAB_INDEX,
        icon: <LegacyFAIcon FAIcon={LegacyFAIcons.Search} />,
        shown: true,
      },
      {
        tabId: SEND_EMAIL_SEARCH_TAB_INDEX,
        href: '',
        label: t('laSendEmail'),
        active: currentTab === SEND_EMAIL_SEARCH_TAB_INDEX,
        icon: <LegacyFAIcon FAIcon={LegacyFAIcons.EnvelopeOutlined} />,
        shown: true,
      },
      {
        tabId: HR_ADMIN_OVERVIEW_SEARCH_TAB_INDEX,
        href: '/page-admin-operations.html',
        label: t('laHRAdminOverview'),
        active: currentTab === HR_ADMIN_OVERVIEW_SEARCH_TAB_INDEX,
        icon: <LegacyFAIcon FAIcon={LegacyFAIcons.Wrench} />,
        shown: isHRAdmin,
      },
    ],
    [
      t,
      currentTab,
      isHRAdmin,
      showMyTeamTab,
      showMatrixTeamTab,
      showNewEmployeeTab,
      showEditableEndedEmployeeTab,
    ],
  );

  const onListItemClick = useCallback(
    async (selectedEmpId: number, selectedEmpUnitId: number) => {
      let fullAccess = false;
      if (!hasFullAccessOverEmp(selectedEmpId, selectedEmpUnitId)) {
        if (await isMgrOrHigherMgr(selectedEmpId)) {
          fullAccess = true;
        }
      } else {
        fullAccess = true;
      }
      let url;

      if (isRoleITAdmin(roles)) {
        url = `/page-peopleITAdmin.html?emp=${selectedEmpId}`;
      } else {
        url = fullAccess
          ? `/page-people.html?emp=${selectedEmpId}`
          : `/page-personalEmp.html?emp=${selectedEmpId}`;
      }

      window.location.assign(url);
    },
    [hasFullAccessOverEmp, isMgrOrHigherMgr, roles],
  );

  const renderUnitRow = useCallback(
    ({ index }: Omit<ListChildComponentProps, 'style' | 'data'>) => {
      const orgGroup = listItems[index] as GroupedOrgUnitEmps;
      const emps = orgGroup && orgGroup.employees ? orgGroup.employees : [];

      return (
        <CategorySearchItem
          key={`org-${orgGroup?.number}`}
          header={`${orgGroup?.name} (${orgGroup?.number})`}
          itemIndex={index}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          list={emps}
          handleClick={onListItemClick}
        />
      );
    },
    [activeIndex, listItems, onListItemClick, setActiveIndex],
  );

  const renderRow = useCallback(
    ({ index, style }: ListChildComponentProps) => {
      const empItem = listItems[index] as Employees;
      const emp = empItem && empItem.fEmpId ? empItem : undefined;

      if (!emp) {
        return null;
      }

      const onClick = () => {
        onListItemClick(emp.fEmpId, emp.fEmpUnit);
      };

      return (
        <SearchedItem
          style={style}
          employee={emp}
          handleClick={onClick}
          showEmpNumber={isHRAdmin}
          showNew={currentTab === RESULTS_SEARCH_TAB_INDEX}
        />
      );
    },
    [currentTab, isHRAdmin, onListItemClick, listItems],
  );

  const dimensions = useSearchDimensions();

  return (
    <>
      {!isPinned && (
        <Backdrop open={isBoxOpen} invisible={true} onClick={() => onClose()} />
      )}
      <InfoBox
        name={t('solaforceHCM')}
        noMargin={true}
        open={isBoxOpen}
        hasPin={true}
        isPinned={isPinned}
        onClose={() => onClose(true)}
        handleBtnAction={onPin}
        shadowLeft={true}
        height={dimensions.infoBox.height}
        actionElement={
          <SearchFooterComponent
            showResults={onSearch}
            openBox={onListToggle}
            isBoxOpen={isBoxOpen}
          />
        }
      >
        <LegacyModuleIconTabs tabs={allTabs} onTabChange={onTabChange}>
          {currentTab !== ORG_UNITS_SEARCH_TAB_INDEX ? (
            <FixedSizeList
              {...dimensions.list}
              itemCount={listItems.length}
              overscanCount={5}
            >
              {renderRow}
            </FixedSizeList>
          ) : (
            <div
              style={{ display: 'block', overflowY: 'auto', height: '100%' }}
            >
              {listItems.map((_item, index) => renderUnitRow({ index }))}
            </div>
          )}
        </LegacyModuleIconTabs>
      </InfoBox>
    </>
  );
};
